/**
 * PlayerTouchMap — "Mapa de toques" panel of the player card (EPIC-107, D1).
 *
 * One dot per touch at the event's `location`, on the same absolute frame the
 * card's other maps use (one frame, home attacks +x, no side flip). The dot
 * set is exactly the events `usePlayerStats` counts as touches that carry a
 * location: authored events not in `TOUCH_EXCLUDED_EVENT_TYPES` and not a
 * shootout kick. Cyan dots with a dark outline; overlapping dots stay
 * individually visible (no blur, no heat).
 */
import React, { useMemo } from "react";
import { PITCH_HEIGHT, PITCH_WIDTH } from "../../utils/heatMapZones";
import {
  PITCH_MARKINGS,
  PITCH_MARKINGS_DERIVED,
} from "../../utils/pitchGeometry";
import { isShootoutKick } from "../../utils/penaltyShootout";
import { TOUCH_EXCLUDED_EVENT_TYPES } from "../../hooks/usePlayerStats";
import type { MatchEvent } from "../../../../store/useMatchLogStore";

interface PlayerTouchMapProps {
  events: MatchEvent[];
  playerId: string;
  t: (key: string, fallback?: string) => string;
  "data-testid"?: string;
}

const interpolateCount = (text: string, count: number): string =>
  text.split("{{count}}").join(String(count));

export const extractTouchPoints = (
  events: MatchEvent[],
  playerId: string,
): { located: [number, number][]; unlocated: number } => {
  const located: [number, number][] = [];
  let unlocated = 0;
  for (const ev of events) {
    if (ev.player_id !== playerId) continue;
    if (TOUCH_EXCLUDED_EVENT_TYPES.has(ev.type) || isShootoutKick(ev)) continue;
    if (ev.location && ev.location.length >= 2) {
      located.push([ev.location[0], ev.location[1]]);
    } else {
      unlocated++;
    }
  }
  return { located, unlocated };
};

const PlayerTouchMap: React.FC<PlayerTouchMapProps> = ({
  events,
  playerId,
  t,
  "data-testid": testId = "player-touch-map",
}) => {
  const { located, unlocated } = useMemo(
    () => extractTouchPoints(events, playerId),
    [events, playerId],
  );
  const count = located.length;
  const { penaltyAreaDepth, penaltyAreaHeight, goalAreaDepth, goalAreaHeight } =
    PITCH_MARKINGS;
  const { penaltyAreaY, goalAreaY } = PITCH_MARKINGS_DERIVED;

  return (
    <div className="flex flex-col gap-2" data-testid={testId}>
      <div className="flex items-baseline justify-between gap-2">
        <h4
          data-testid={`${testId}-title`}
          className="text-xs font-semibold text-slate-200 uppercase tracking-wider"
        >
          {t("analytics.touchMapTitle", "Touch Map")}
        </h4>
        <span
          data-testid={`${testId}-count`}
          className="text-xs text-slate-200 tabular-nums"
        >
          {interpolateCount(
            count === 1
              ? t("analytics.touchMapCountOne", "{{count}} touch")
              : t("analytics.touchMapCount", "{{count}} touches"),
            count,
          )}
        </span>
      </div>
      <div
        className="relative w-full max-w-md rounded-lg border border-slate-700 bg-green-800 overflow-hidden"
        style={{ aspectRatio: `${PITCH_WIDTH + 4} / ${PITCH_HEIGHT + 4}` }}
      >
        <svg
          viewBox={`-2 -2 ${PITCH_WIDTH + 4} ${PITCH_HEIGHT + 4}`}
          className="absolute inset-0 w-full h-full"
          preserveAspectRatio="xMidYMid meet"
          role="img"
          aria-label={t("analytics.touchMapTitle", "Touch Map")}
          data-testid={`${testId}-svg`}
        >
          <rect
            x={0}
            y={0}
            width={PITCH_WIDTH}
            height={PITCH_HEIGHT}
            fill="#2d6a2e"
            rx={1}
          />
          <g stroke="rgba(255,255,255,0.55)" strokeWidth={0.4} fill="none">
            <rect x={0} y={0} width={PITCH_WIDTH} height={PITCH_HEIGHT} />
            <line
              x1={PITCH_WIDTH / 2}
              y1={0}
              x2={PITCH_WIDTH / 2}
              y2={PITCH_HEIGHT}
            />
            <ellipse
              cx={PITCH_WIDTH / 2}
              cy={PITCH_HEIGHT / 2}
              rx={PITCH_MARKINGS.centerCircleRx}
              ry={PITCH_MARKINGS.centerCircleRy}
            />
            <rect
              x={0}
              y={penaltyAreaY}
              width={penaltyAreaDepth}
              height={penaltyAreaHeight}
            />
            <rect
              x={PITCH_WIDTH - penaltyAreaDepth}
              y={penaltyAreaY}
              width={penaltyAreaDepth}
              height={penaltyAreaHeight}
            />
            <rect
              x={0}
              y={goalAreaY}
              width={goalAreaDepth}
              height={goalAreaHeight}
            />
            <rect
              x={PITCH_WIDTH - goalAreaDepth}
              y={goalAreaY}
              width={goalAreaDepth}
              height={goalAreaHeight}
            />
          </g>
          <g data-testid={`${testId}-dots`}>
            {located.map(([x, y], i) => (
              <circle
                key={i}
                data-testid={`${testId}-dot`}
                cx={x}
                cy={y}
                r={1.3}
                fill="#22d3ee"
                stroke="#0f172a"
                strokeWidth={0.4}
              />
            ))}
          </g>
        </svg>
      </div>
      {unlocated > 0 && (
        <p
          data-testid={`${testId}-unlocated`}
          className="text-xs text-slate-200"
        >
          {interpolateCount(
            t("analytics.touchMapUnlocated", "{{count}} without location"),
            unlocated,
          )}
        </p>
      )}
    </div>
  );
};

export default PlayerTouchMap;
